import React, { useState, useEffect } from 'react';
import { fetchFeedbackSummary, fetchFeedbackDetails } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import { toTitleCase } from '../utils/format';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];
const PAGE_SIZE = 50;

const STAR_COLORS = { 5: '#1a8a4a', 4: '#4da36e', 3: '#b06a00', 2: '#c0392b', 1: '#c0392b' };

function StarBadge({ score }) {
  const n = Number(score ?? 0);
  const color = n >= 4.5 ? '#1a8a4a' : n >= 3.5 ? '#b06a00' : '#c0392b';
  return (
    <span style={{ fontWeight: 700, fontSize: 13, color }}>
      {n.toFixed(1)} ★
    </span>
  );
}

export default function Feedback({ navigateTo }) {
  const [tab, setTab] = useState('summary');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);
  const [ratingFrom, setRatingFrom] = useState('');
  const [ratingTo, setRatingTo] = useState('');
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    setError(null);
    const baseParams = {
      FeedbackFromDate: toApiDate(start),
      FeedbackToDate: toApiDate(end),
      PageNumber: page,
      PageSize: PAGE_SIZE,
      Type: 0,
      ...(ratingFrom !== '' ? { RatingFrom: Number(ratingFrom) } : {}),
      ...(ratingTo !== '' ? { RatingTo: Number(ratingTo) } : {}),
    };
    const fn = tab === 'summary' ? fetchFeedbackSummary : fetchFeedbackDetails;
    fn(baseParams)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [tab, page]);

  const rows = Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [];
  const total = data?.totalRows ?? data?.total ?? rows.length;
  const summary = !Array.isArray(data) ? (data?.summary ?? null) : null;

  // Compute averages for summary view
  const avgScore = rows.length > 0
    ? rows.reduce((s, r) => s + Number(r.overallRate ?? r.rating ?? r.score ?? 0), 0) / rows.length
    : 0;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Customer Feedback</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Filters</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>MIN RATING</div>
            <input type="number" min="1" max="5" step="0.1" value={ratingFrom} onChange={e => setRatingFrom(e.target.value)} placeholder="1" className="form-input" style={{ fontSize: 11, width: 70 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>MAX RATING</div>
            <input type="number" min="1" max="5" step="0.1" value={ratingTo} onChange={e => setRatingTo(e.target.value)} placeholder="5" className="form-input" style={{ fontSize: 11, width: 70 }} />
          </div>
          <button onClick={() => { setPage(1); load(); }} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 2, marginBottom: 12 }}>
        {['summary', 'details'].map(t => (
          <button key={t} onClick={() => { setTab(t); setPage(1); setData(null); }} style={{ fontSize: 11, padding: '6px 16px', borderRadius: '6px 6px 0 0', border: 'none', cursor: 'pointer', fontWeight: 700, background: tab === t ? 'var(--navy)' : 'var(--gray-200)', color: tab === t ? '#fff' : 'var(--gray-600)' }}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          {tab === 'summary' ? 'Feedback Summary' : 'Feedback Details'}
          {total > 0 && ` (${total.toLocaleString()})`}
          {tab === 'summary' && rows.length > 0 && (
            <span style={{ marginLeft: 12 }}><StarBadge score={avgScore} /></span>
          )}
        </div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No feedback found.</div>
        )}

        {tab === 'summary' && rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Product</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Packaging</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Delivery</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Service</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Overall</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} onClick={() => navigateTo?.('order-details', row.bcOrderId ?? row.orderId)} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? '—'}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{toTitleCase(row.billTo ?? row.customerName ?? '—')}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.prodRate ?? row.productRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.packRate ?? row.packagingRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.delivRate ?? row.deliveryRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.servRate ?? row.serviceRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.overallRate ?? row.overall ?? row.rating} /></td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                      {row.feedBackDate ?? row.feedbackDate ? new Date(row.feedBackDate ?? row.feedbackDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'details' && rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Rating</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Comment</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Packer</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} onClick={() => navigateTo?.('order-details', row.bcOrderId ?? row.orderId)} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? '—'}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{toTitleCase(row.billTo ?? row.customerName ?? '—')}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.overallRate ?? row.rating} /></td>
                    <td style={{ padding: '8px 8px', color: 'var(--gray-700)', maxWidth: 280 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.comment ?? row.feedback ?? '—'}</div>
                    </td>
                    <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>{row.packer ?? row.packerName ?? '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                      {row.feedBackDate ?? row.feedbackDate ? new Date(row.feedBackDate ?? row.feedbackDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows.length > 0 && total > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>← Prev</button>
            <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}
